/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { PrimitiveAtom, atom } from 'jotai';
import { atomFamily } from 'jotai/utils';
import { ColorObject } from 'jotai/vehicleColor';
import { createCustomColorFromId } from 'jotai/vehicleColor/ColorObject';

const createDefaultColor = (colorId?: string) => {
  if (colorId?.includes('custom') && colorId?.split('-')[1]?.length > 0) return createCustomColorFromId(colorId);
  return {
    id: colorId,
    name: colorId,
    group: 'neutral',
    settingsId: '',
    hex: '#FFFFFF',
    rgb: [255, 255, 255],
    contrastTextHex: '#000000',
    contrastTextRGB: [0, 0, 0],
  } as ColorObject;
};

// Atom for storing the list of custom colors
export const customColorAtomFamily = atomFamily<string | undefined, PrimitiveAtom<ColorObject>>(
  (id: string | undefined) => atom<ColorObject>(createDefaultColor(id))
);

export const colorIdsAtom = atom<string[]>([]);

export const customColorsAccessSelectorFamily = atomFamily((id: string | undefined) =>
  atom<ColorObject, [ColorObject], void>(
    (get) => {
      return get(customColorAtomFamily(id));
    },
    (_get, set, customColor: ColorObject) => {
      const defaultColor = createDefaultColor(id);
      if (customColor.id === defaultColor.id) {
        set(customColorAtomFamily(id), defaultColor);
        set(colorIdsAtom, (prevValue) => prevValue.filter((i) => i !== id));
      } else {
        // creates the atom and update the ids list
        if (id) {
          set(customColorAtomFamily(id), customColor);
          set(colorIdsAtom, (prev) => [...prev, id]);
        }
      }
    }
  )
);

// Color selector that will return all custom colors in Ids inside an array
export const allCustomColorsSelector = atom<ColorObject[]>((get) => {
  const colorIdArray = get(colorIdsAtom);
  return colorIdArray.map((id) => get(customColorAtomFamily(id)));
});
